/**
 * How the creature is feeling.
 *
 * This is the rule book for the whole character: given what Sam has logged so
 * far today, it decides which face to wear and what to say. It is deliberately
 * one small, pure function with no React and no network in it, so it is easy
 * to read, easy to change, and easy to try out.
 *
 * ---------------------------------------------------------------------------
 * If you want to change the creature's personality, this is the file.
 *
 *   - to change what it SAYS, edit the `lines` for a mood below
 *   - to change WHEN it feels something, edit the order of the checks in
 *     `moodFor` -- the first one that matches wins, so the ones at the top
 *     are the ones that matter most
 *   - to change what it says while eating, sipping or pooping, edit `momentLines`
 *   - to change which food it eats or what it sips, edit `foods` and `drinks`
 *   - to change how it LOOKS, see components/Creature.tsx instead
 *   - to change how often it poops, see lib/poops.ts
 *
 * One rule worth keeping: it is never mean. It notices, it encourages, and it
 * is pleased to see her. It never scolds her for what she ate, and it never
 * implies she has failed at anything.
 */
import type { Day } from "./api";

export type Mood =
  | "asleep"
  | "waiting"
  | "happy"
  | "proud"
  | "strong"
  | "thirsty"
  | "sleepy"
  | "full";

export type Feeling = {
  mood: Mood;
  /** One short line, in the creature's voice. */
  line: string;
};

/**
 * What it might say in each mood. One is picked per day rather than at random
 * on every render, so the creature doesn't appear to change its mind every
 * time the screen redraws.
 */
const lines: Record<Mood, string[]> = {
  asleep: ["Still dreaming about toast…", "Zzz… morning already?", "Five more minutes."],
  waiting: ["Morning! What's for breakfast?", "I'm ready when you are.", "Ooh, is it snack o'clock?"],
  happy: ["This is a good day.", "Nice balance so far!", "We're doing great."],
  proud: ["You MOVED today. I saw it.", "Look at you go!", "That walk counted. I counted it."],
  strong: ["So much protein. I feel enormous.", "Strong day!", "Protein goal: demolished."],
  thirsty: ["Have you had water? Be honest.", "A glass of water would be lovely.", "Hydrate, please!"],
  sleepy: ["Long day. Rest well.", "Winding down…", "Good night from me."],
  full: ["Comfortably stuffed.", "That was a lot of food. No notes.", "I need a lie down. Happily."],
};

/**
 * Picks one of the lines, but the same one all day.
 *
 * The date and mood together decide which, so the line only changes when the
 * mood does -- which makes the creature feel like it has a thought, rather
 * than like it is shuffling cards.
 */
function lineFor(mood: Mood, date: string): string {
  return pick(lines[mood], date + mood);
}

function pick(options: string[], key: string): string {
  let hash = 0;
  for (const character of key) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return options[hash % options.length];
}

/** Something happening right now, which takes over for a couple of seconds. */
export type MomentKind = "eating" | "sipping" | "pooping" | "thanks";

export type Moment = {
  kind: MomentKind;
  line: string;
  /** How long it lasts. Matches the animations in styles.css. */
  ms: number;
  /** The little picture of what it is eating or drinking. */
  food?: string;
};

const momentLines: Record<MomentKind, string[]> = {
  eating: ["Nom nom nom!", "Ooh, delicious.", "Mmm! Thank you!", "Yes. This. More of this."],
  sipping: ["Slurrrp.", "Ahh, refreshing.", "Glug glug glug.", "Mmm. Just what I needed."],
  pooping: ["Oop. Excuse me.", "Nothing to see here.", "That one's for you."],
  thanks: ["Ahh, much better!", "My hero.", "So fresh. So clean.", "Thanks! You're the best."],
};

const momentLengths: Record<MomentKind, number> = {
  eating: 2800,
  sipping: 2600,
  pooping: 1400,
  thanks: 1600,
};

/** What it says while there is a poop lying around. */
const poopAroundLines = ["Um. I left you something.", "It wasn't me. (It was me.)", "Psst. Tap it?"];

export function poopAroundLine(date: string): string {
  return pick(poopAroundLines, date);
}

/**
 * The little food that flies into its mouth. The first word found in the name
 * of what she logged wins, so more specific words go first ("pineapple" before
 * "apple"). Anything it doesn't recognise gets a surprise from `snacks`.
 */
const foods: [string, string][] = [
  ["pizza", "🍕"], ["burger", "🍔"], ["fries", "🍟"], ["taco", "🌮"], ["burrito", "🌯"],
  ["sandwich", "🥪"], ["toast", "🍞"], ["bread", "🍞"], ["bagel", "🥯"], ["croissant", "🥐"],
  ["egg", "🍳"], ["banana", "🍌"], ["pineapple", "🍍"], ["apple", "🍎"], ["berr", "🍓"],
  ["orange", "🍊"], ["grape", "🍇"], ["avocado", "🥑"], ["carrot", "🥕"], ["broccoli", "🥦"],
  ["salad", "🥗"], ["sushi", "🍣"], ["rice", "🍚"], ["pasta", "🍝"], ["spaghetti", "🍝"],
  ["noodle", "🍜"], ["ramen", "🍜"], ["soup", "🍲"], ["chicken", "🍗"], ["steak", "🥩"],
  ["beef", "🥩"], ["bacon", "🥓"], ["fish", "🐟"], ["salmon", "🐟"], ["tuna", "🐟"],
  ["cheese", "🧀"], ["cookie", "🍪"], ["cake", "🍰"], ["chocolate", "🍫"], ["donut", "🍩"],
  ["ice cream", "🍦"], ["yogurt", "🥣"], ["yoghurt", "🥣"], ["oat", "🥣"], ["cereal", "🥣"], ["nut", "🥜"],
];
const snacks = ["🍎", "🥕", "🍪", "🍙", "🍓"];

/** The same idea for drinks. */
const drinks: [string, string][] = [
  ["water", "💧"], ["coffee", "☕"], ["latte", "☕"], ["espresso", "☕"], ["cappuccino", "☕"],
  ["boba", "🧋"], ["bubble tea", "🧋"], ["tea", "🍵"], ["matcha", "🍵"], ["milk", "🥛"],
  ["juice", "🧃"], ["beer", "🍺"], ["wine", "🍷"], ["cocktail", "🍹"], ["coconut", "🥥"],
];
const sips = ["🥤", "🧃"];

function pictureFor(name: string, table: [string, string][], surprises: string[]): string {
  const lower = name.toLowerCase();
  return table.find(([word]) => lower.includes(word))?.[1] ?? randomFrom(surprises);
}

function randomFrom(options: string[]): string {
  return options[Math.floor(Math.random() * options.length)];
}

export function momentOf(kind: "pooping" | "thanks"): Moment {
  return { kind, line: randomFrom(momentLines[kind]), ms: momentLengths[kind] };
}

export function eat(foodName: string): Moment {
  return {
    kind: "eating",
    line: randomFrom(momentLines.eating),
    ms: momentLengths.eating,
    food: pictureFor(foodName, foods, snacks),
  };
}

export function sip(drinkName: string): Moment {
  return {
    kind: "sipping",
    line: randomFrom(momentLines.sipping),
    ms: momentLengths.sipping,
    food: pictureFor(drinkName, drinks, sips),
  };
}

/**
 * Works out how the creature feels about a day.
 *
 * `hour` is the hour of the day, 0-23, passed in rather than read from the
 * clock so this can be tested and tried out at any hour.
 */
export function moodFor(day: Day, hour: number): Feeling {
  const { totals, settings, entries, burned, movedMinutes } = day;
  const eaten = totals.calories;
  const goal = settings.goal_calories;

  const drinks = entries.filter((entry) => entry.kind === "drink").length;
  const hasEaten = entries.some((entry) => entry.kind !== "exercise");

  // The order below IS the personality. The first match wins.
  const mood: Mood = (() => {
    // Before anything has happened, the creature's state is about the hour,
    // not the food.
    if (!hasEaten && hour < 10) return "asleep";
    if (!hasEaten) return "waiting";

    // Movement is the thing it is most pleased about, so it gets to interrupt.
    if ((burned > 0 || movedMinutes > 0) && hour >= 10) return "proud";

    // Comfortably past the calorie goal, in a pleased way rather than a
    // disapproving one.
    if (eaten > goal * 1.15) return "full";

    // Protein met is worth celebrating on its own.
    if (totals.protein_g >= settings.goal_protein_g) return "strong";

    // A gentle nudge, only once the day is well under way.
    if (drinks === 0 && hour >= 13) return "thirsty";

    if (hour >= 21) return "sleepy";

    return "happy";
  })();

  return { mood, line: lineFor(mood, day.date) };
}

/**
 * How far round the ring to draw, 0 to 1, with anything past the goal clamped
 * so the ring fills rather than wrapping round again.
 */
export function progress(value: number, goal: number): number {
  if (!Number.isFinite(goal) || goal <= 0) return 0;
  return Math.max(0, Math.min(1, value / goal));
}
